import { useRef } from "react";
import ActionItem from "./ActionItem";
import type { ActionItem as ActionItemType, EditingActionItem } from "./types";

interface ActionItemListProps {
  items: ActionItemType[];
  projectId: number; // -1 for global action items
  onAddNew: (projectId: number) => void;
  onEdit: (projectId: number, actionIndex: number, fieldIndex: number) => void;
  onStatusToggle?: (
    projectId: number,
    actionIndex: number,
    newStatus: "actionable" | "done" | "not actionable",
  ) => void;
  onFieldChange: (
    field: keyof EditingActionItem,
    value: string | number,
  ) => void;
  onSave: () => void;
  onCancel: () => void;
  focusState: {
    itemIndex: number;
    isAddingNew: boolean;
  };
  editingItem?: EditingActionItem;
  title?: string;
  className?: string;
}

export default function ActionItemList({
  items,
  projectId,
  onAddNew,
  onEdit,
  onStatusToggle,
  onFieldChange,
  onSave,
  onCancel,
  focusState,
  editingItem,
  title,
  className = "",
}: ActionItemListProps) {
  const contentRef = useRef<HTMLInputElement>(null);
  const statusRef = useRef<HTMLSelectElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const delegatedRef = useRef<HTMLInputElement>(null);

  // Sort action items by status and creation date
  const sortedItems = [...items].sort((a, b) => {
    // Define status priority: actionable > not actionable > done
    const statusPriority = {
      actionable: 0,
      "not actionable": 1,
      done: 2,
    };

    // First sort by status
    const statusComparison =
      statusPriority[a.status] - statusPriority[b.status];
    if (statusComparison !== 0) {
      return statusComparison;
    }

    // Within same status, sort by creation date (most recent first)
    const aDate = new Date(a.createdAt || "").getTime() || 0;
    const bDate = new Date(b.createdAt || "").getTime() || 0;
    return bDate - aDate; // Descending order (most recent first)
  });

  const handleNewItemKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  const isEditingNewItem =
    editingItem?.projectId === projectId && editingItem?.actionIndex === -1;
  const isAddNewFocused = focusState.isAddingNew;

  return (
    <div className={className}>
      {title && <h4 className="font-medium mb-3 text-gray-700">{title}</h4>}
      <div className="space-y-2">
        {/* Add New Action Item Card */}
        <div
          className={`p-3 rounded border-2 border-dashed cursor-pointer focus:outline-none ${
            isAddNewFocused
              ? "ring-2 ring-green-300 bg-green-50 border-green-300"
              : "border-gray-300 hover:border-green-300 bg-gray-50"
          }`}
          data-new-action-item
          data-project-id={projectId}
          tabIndex={0}
          onClick={() => onAddNew(projectId)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAddNew(projectId);
            }
          }}
        >
          {isEditingNewItem ? (
            <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
              <input
                ref={contentRef}
                type="text"
                placeholder="New action item content"
                value={editingItem?.content || ""}
                onChange={(e) => onFieldChange("content", e.target.value)}
                onKeyDown={handleNewItemKeyDown}
                className="w-full p-2 border border-green-500 rounded"
              />
              <div className="flex gap-2">
                <select
                  ref={statusRef}
                  value={editingItem?.status || "actionable"}
                  onChange={(e) => onFieldChange("status", e.target.value)}
                  className="p-2 border border-green-500 rounded"
                >
                  <option value="actionable">Actionable</option>
                  <option value="done">Done</option>
                  <option value="not actionable">Not Actionable</option>
                </select>
                <input
                  ref={dateRef}
                  type="date"
                  value={editingItem?.date || ""}
                  onChange={(e) => onFieldChange("date", e.target.value)}
                  className="p-2 border border-green-500 rounded"
                />
                <input
                  ref={delegatedRef}
                  type="text"
                  placeholder="Delegated to"
                  value={editingItem?.delegatedTo || ""}
                  onChange={(e) => onFieldChange("delegatedTo", e.target.value)}
                  className="flex-1 p-2 border border-green-500 rounded"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center text-gray-500">
              <span className="text-lg">+ Add New Action Item</span>
            </div>
          )}
        </div>

        {/* Existing Action Items */}
        {sortedItems.map((actionItem) => {
          // Find the original index in the unsorted items array
          const originalIndex = items.findIndex(
            (item) => item.id === actionItem.id,
          );

          return (
            <ActionItem
              key={actionItem.id}
              actionItem={actionItem}
              actionIndex={originalIndex}
              projectId={projectId}
              onEdit={onEdit}
              onStatusToggle={onStatusToggle}
              isEditing={
                editingItem?.projectId === projectId &&
                editingItem?.actionIndex === originalIndex
              }
              editData={editingItem}
              onFieldChange={onFieldChange}
              onSave={onSave}
              onCancel={onCancel}
              isFocused={
                focusState.itemIndex === originalIndex &&
                !focusState.isAddingNew
              }
            />
          );
        })}
      </div>
    </div>
  );
}
